import type { RosControlSocketLike } from "lib/ros/rosTopicSubscribeWithRetry";
import {
    attachRosTopicWithRetry,
    LATE_ROS_TOPIC_OPTIONS,
} from "lib/ros/rosTopicSubscribeWithRetry";

export const TOPIC_STATE_CONNECTED = "/state/connected";

/** Default how long Connect waits for `/state/connected === true`. */
export const ROBOT_LINK_FEEDBACK_TIMEOUT_MS = 120_000;

export function parseConnectedPayload(data: unknown): boolean | null {
    if (data == null) return null;
    if (typeof data === "boolean") return data;
    if (typeof data === "object" && data !== null && "data" in data) {
        const inner = (data as { data: unknown }).data;
        if (typeof inner === "boolean") return inner;
    }
    return null;
}

/**
 * Resolve when the robot bridge publishes `/state/connected` with data=true.
 * Rejects on AbortSignal, timeout, or missing controlSocket.
 */
export function waitForRobotLinkConnected(
    controlSocket: RosControlSocketLike | null | undefined,
    options?: { signal?: AbortSignal; timeoutMs?: number }
): Promise<void> {
    const timeoutMs = options?.timeoutMs ?? ROBOT_LINK_FEEDBACK_TIMEOUT_MS;
    const signal = options?.signal;

    if (!controlSocket) {
        return Promise.reject(new Error("ARC control socket not connected"));
    }

    return new Promise((resolve, reject) => {
        let settled = false;
        let detach: (() => void) | null = null;
        let timeoutId: number | null = null;

        const cleanup = () => {
            if (timeoutId != null) {
                window.clearTimeout(timeoutId);
                timeoutId = null;
            }
            if (detach) {
                detach();
                detach = null;
            }
            signal?.removeEventListener("abort", onAbort);
        };

        const finishOk = () => {
            if (settled) return;
            settled = true;
            cleanup();
            resolve();
        };

        const finishErr = (err: Error) => {
            if (settled) return;
            settled = true;
            cleanup();
            reject(err);
        };

        const onAbort = () => {
            finishErr(new DOMException("Connect cancelled", "AbortError"));
        };

        if (signal?.aborted) {
            onAbort();
            return;
        }
        signal?.addEventListener("abort", onAbort);

        timeoutId = window.setTimeout(() => {
            finishErr(new Error("timeout"));
        }, timeoutMs);

        detach = attachRosTopicWithRetry(
            controlSocket,
            TOPIC_STATE_CONNECTED,
            (data) => {
                if (parseConnectedPayload(data) === true) {
                    finishOk();
                }
            },
            {
                ...LATE_ROS_TOPIC_OPTIONS,
                messageTypeFallback: "std_msgs/msg/Bool",
                subscribeQos: {
                    durability: "transient_local",
                    reliability: "reliable",
                    history: "keep_last",
                    depth: 1,
                },
                markMessageReceived: (data) => parseConnectedPayload(data) !== null,
            }
        );
    });
}
